import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FormRenderer } from '@/forms/FormRenderer';
import { FormSubmissionView } from '@/forms/FormSubmissionView';
import {
  toEnvelope,
  useAttachJobForm,
  useDetachJobForm,
  useFormSubmission,
  useFormTemplate,
  useJobFormTemplates,
  useJobForms,
  useSaveJobForm,
  type JobForm,
} from './useJobForms';

/**
 * The forms attached to one job (M1-20) — the differentiator becoming visible.
 *
 * Its own component rather than more of `JobDetail.tsx` (already 580 lines):
 * this is a self-contained section with four of its own queries, and the page
 * only needs to place it.
 *
 * <b>Filling happens inline, not in a modal.</b> Every other write on this page
 * uses `Dialog`, but a dynamic form is arbitrarily long — a checklist with a
 * signature pad and a repeating parts table inside a focus-trapped modal on a
 * phone is the worst version of this screen, and the reader for this one is a
 * technician standing in a crawlspace. The attach picker IS a dialog: it is
 * three controls, and it is an office action.
 */
export function JobForms({ jobId, canAttach }: { jobId: string; canAttach: boolean }) {
  const { t } = useTranslation();
  const forms = useJobForms(jobId);
  const [openTemplateId, setOpenTemplateId] = useState<string | null>(null);
  const [viewingSubmissionId, setViewingSubmissionId] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  return (
    <section className="space-y-3" aria-labelledby="job-forms-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="job-forms-heading" className="text-base font-semibold">
          {t('jobForms.heading')}
        </h2>
        {canAttach ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setAttaching(true)}>
            {t('jobForms.attach')}
          </Button>
        ) : null}
      </div>

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {forms.isPending ? <LoadingState label={t('jobForms.loading')} /> : null}

      {forms.isError ? (
        <ErrorState
          title={t('jobForms.loadErrorTitle')}
          description={t('jobForms.loadErrorDescription')}
          onRetry={() => void forms.refetch()}
        />
      ) : null}

      {forms.data?.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('jobForms.none')}</p>
      ) : null}

      <ul className="space-y-2">
        {forms.data?.map((form) => (
          <li
            key={form.formTemplateKey}
            className="rounded-md border border-[var(--color-border)] p-3"
          >
            <JobFormRow
              jobId={jobId}
              form={form}
              canAttach={canAttach}
              isOpen={openTemplateId === form.formTemplateKey}
              onToggleFill={(open) => setOpenTemplateId(open ? form.formTemplateKey : null)}
              onView={() => setViewingSubmissionId(form.submissionId ?? null)}
              onAnnounce={setAnnouncement}
            />
          </li>
        ))}
      </ul>

      {attaching ? (
        <AttachFormDialog
          jobId={jobId}
          onClose={() => setAttaching(false)}
          onAttached={(name) => {
            setAttaching(false);
            setAnnouncement(t('jobForms.attached', { name }));
          }}
        />
      ) : null}

      {viewingSubmissionId ? (
        <SubmissionDialog
          submissionId={viewingSubmissionId}
          onClose={() => setViewingSubmissionId(null)}
        />
      ) : null}
    </section>
  );
}

function JobFormRow({
  jobId,
  form,
  canAttach,
  isOpen,
  onToggleFill,
  onView,
  onAnnounce,
}: {
  jobId: string;
  form: JobForm;
  canAttach: boolean;
  isOpen: boolean;
  onToggleFill: (open: boolean) => void;
  onView: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const detach = useDetachJobForm(jobId);
  const name = form.formTemplateName ?? form.formTemplateKey;
  const submitted = form.submissionStatus === 'Submitted';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="space-y-0.5">
          <p className="text-sm font-medium">{name}</p>
          <p className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
            {/* Status is words, never colour alone (web/CONVENTIONS.md's CC-13
                standard) — a chip whose only signal is hue is unreadable to a
                colourblind dispatcher. */}
            <span>{t(`jobForms.state.${form.submissionStatus ?? 'NotStarted'}`)}</span>
            {form.required ? (
              <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5">
                {t('jobForms.required')}
              </span>
            ) : null}
            {form.fromJobType ? <span>{t('jobForms.fromJobType')}</span> : null}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {submitted ? (
            <Button type="button" size="sm" variant="outline" onClick={onView}>
              {t('jobForms.view')}
            </Button>
          ) : form.formTemplateId ? (
            <Button
              type="button"
              size="sm"
              variant={isOpen ? 'ghost' : 'default'}
              aria-expanded={isOpen}
              onClick={() => onToggleFill(!isOpen)}
            >
              {isOpen
                ? t('common.cancel')
                : form.submissionStatus === 'Draft'
                  ? t('jobForms.continue')
                  : t('jobForms.fillIn')}
            </Button>
          ) : null}

          {canAttach && form.jobAssignmentId ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={detach.isPending}
              onClick={() =>
                detach.mutate(form.jobAssignmentId!, {
                  onSuccess: () => onAnnounce(t('jobForms.detached', { name })),
                  onError: () => onAnnounce(t('jobForms.detachFailed')),
                })
              }
            >
              {t('jobForms.detach')}
            </Button>
          ) : null}
        </div>
      </div>

      {/* A required form whose template has no published version left. Surfaced
          rather than hidden: it still blocks completing the job, and a refusal
          the page cannot explain is worse than an awkward message. */}
      {!form.formTemplateId && !submitted ? (
        <p role="alert" className="text-xs text-[var(--color-destructive)]">
          {t('jobForms.noPublishedVersion')}
        </p>
      ) : null}

      {isOpen && form.formTemplateId ? (
        <FillForm
          jobId={jobId}
          templateId={form.formTemplateId}
          submissionId={form.submissionStatus === 'Draft' ? (form.submissionId ?? undefined) : undefined}
          onDone={(message) => {
            onToggleFill(false);
            onAnnounce(message);
          }}
          onAnnounce={onAnnounce}
        />
      ) : null}
    </div>
  );
}

/**
 * The live form: the template's schema, this job's draft values if there are
 * any, and the two ways out — save a draft, or submit.
 */
function FillForm({
  jobId,
  templateId,
  submissionId,
  onDone,
  onAnnounce,
}: {
  jobId: string;
  templateId: string;
  submissionId?: string;
  onDone: (message: string) => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const template = useFormTemplate(templateId);
  // Only fetched when continuing a draft — a fresh form has nothing to restore.
  const draft = useFormSubmission(submissionId);
  const save = useSaveJobForm(jobId);

  if (template.isPending || (submissionId && draft.isPending)) {
    return <LoadingState label={t('jobForms.loadingForm')} />;
  }

  if (template.isError || !template.data) {
    return (
      <ErrorState
        title={t('jobForms.formErrorTitle')}
        description={t('jobForms.formErrorDescription')}
        onRetry={() => void template.refetch()}
      />
    );
  }

  const initialValues = (draft.data?.data as Record<string, unknown> | undefined) ?? undefined;

  return (
    <div className="rounded-md bg-[var(--color-muted)] p-3">
      <FormRenderer
        envelope={toEnvelope(template.data.schema)}
        initialValues={initialValues}
        onSaveDraft={(values) =>
          new Promise<void>((resolve) => {
            save.mutate(
              { formTemplateId: templateId, submissionId, values, submit: false },
              {
                onSuccess: () => {
                  onAnnounce(t('jobForms.draftSaved'));
                  resolve();
                },
                onError: () => {
                  onAnnounce(t('jobForms.saveFailed'));
                  resolve();
                },
              },
            );
          })
        }
        onSubmit={(values) =>
          new Promise<void>((resolve) => {
            save.mutate(
              { formTemplateId: templateId, submissionId, values, submit: true },
              {
                onSuccess: () => {
                  onDone(t('jobForms.submitted'));
                  resolve();
                },
                onError: () => {
                  onAnnounce(t('jobForms.saveFailed'));
                  resolve();
                },
              },
            );
          })
        }
      />
    </div>
  );
}

function SubmissionDialog({ submissionId, onClose }: { submissionId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const submission = useFormSubmission(submissionId);
  const template = useFormTemplate(submission.data?.formTemplateId);

  return (
    <Dialog
      titleId="job-form-submission-title"
      title={submission.data?.formTemplateName ?? t('jobForms.heading')}
      onClose={onClose}
      testId="submission-dialog"
      footer={
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {submission.isPending || template.isPending ? (
        <LoadingState label={t('jobForms.loadingForm')} />
      ) : submission.data && template.data ? (
        <FormSubmissionView
          envelope={toEnvelope(template.data.schema)}
          values={(submission.data.data as Record<string, unknown>) ?? {}}
        />
      ) : (
        <ErrorState
          title={t('jobForms.formErrorTitle')}
          description={t('jobForms.formErrorDescription')}
        />
      )}
    </Dialog>
  );
}

function AttachFormDialog({
  jobId,
  onClose,
  onAttached,
}: {
  jobId: string;
  onClose: () => void;
  onAttached: (name: string) => void;
}) {
  const { t } = useTranslation();
  const templates = useJobFormTemplates(true);
  const attach = useAttachJobForm(jobId);
  const [templateKey, setTemplateKey] = useState('');
  const [required, setRequired] = useState(false);
  const [error, setError] = useState('');

  // One entry per template FAMILY: the picker chooses what to attach, and an
  // assignment names the family, not a version (the current published one is
  // resolved server-side at read time).
  const families = Array.from(
    new Map((templates.data ?? []).map((template) => [template.key, template])).values(),
  );

  return (
    <Dialog
      titleId="attach-form-title"
      title={t('jobForms.attachTitle')}
      onClose={onClose}
      testId="attach-form-dialog"
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!templateKey || attach.isPending}
            onClick={() => {
              const chosen = families.find((family) => family.key === templateKey);
              attach.mutate(
                { formTemplateKey: templateKey, required },
                {
                  onSuccess: () => onAttached(chosen?.name ?? templateKey),
                  // The API's own words — "this form is already attached here"
                  // is a sentence someone can act on; "couldn't attach" is not.
                  onError: (failure) => setError(failure.message),
                },
              );
            }}
          >
            {t('jobForms.attachConfirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <label htmlFor="attach-form-template" className="text-sm font-medium">
            {t('jobForms.templateLabel')}
          </label>
          <select
            id="attach-form-template"
            value={templateKey}
            onChange={(event) => setTemplateKey(event.target.value)}
            className="h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          >
            <option value="">{t('jobForms.templatePlaceholder')}</option>
            {families.map((family) => (
              <option key={family.key} value={family.key}>
                {family.name}
              </option>
            ))}
          </select>
          {templates.data?.length === 0 ? (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {t('jobForms.noTemplates')}
            </p>
          ) : null}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
          />
          {t('jobForms.requiredLabel')}
        </label>

        {error ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
